import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { formatDateTimeIST, formatDateIST } from "../utils/dateUtils";
import { 
  GitCompare, 
  FileDown, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ArrowRight
} from "lucide-react";

export default function ComparisonPage() {
  const { token } = useAuth();
  const [tests, setTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [comparison, setComparison] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchTests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tests`, { headers });
      const completedTests = response.data.filter(t => t.status !== "in_progress");
      setTests(completedTests);
    } catch (error) {
      toast.error("Failed to load tests");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleSelectTest = (testId) => {
    setSelectedTests(prev => {
      if (prev.includes(testId)) {
        return prev.filter(id => id !== testId);
      }
      if (prev.length >= 10) {
        toast.error("Maximum 10 tests can be compared");
        return prev;
      }
      return [...prev, testId];
    });
    // Clear previous comparison when selection changes
    setComparison(null);
  };

  const handleCompare = async () => {
    if (selectedTests.length < 2) {
      toast.error("Please select at least 2 tests to compare");
      return;
    }

    setComparing(true);
    try {
      const response = await axios.post(`${API}/tests/compare`, {
        test_ids: selectedTests
      }, { headers });
      setComparison(response.data);
      toast.success("Comparison generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate comparison");
    } finally {
      setComparing(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (selectedTests.length < 2) {
      toast.error("Please select at least 2 tests");
      return;
    }

    setDownloadingPdf(true);
    try {
      const response = await axios.post(`${API}/tests/compare/pdf`, {
        test_ids: selectedTests
      }, { 
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "passed") {
      return <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" />Passed</Badge>;
    }
    return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comparison-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Test Comparison</h2>
          <p className="text-muted-foreground">Compare multiple test sessions to identify patterns</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleCompare} 
            disabled={selectedTests.length < 2 || comparing}
            data-testid="compare-btn"
          >
            {comparing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <GitCompare className="w-4 h-4 mr-2" />
            )}
            Compare ({selectedTests.length})
          </Button>
          <Button 
            variant="outline"
            onClick={handleDownloadPdf} 
            disabled={selectedTests.length < 2 || downloadingPdf}
            data-testid="download-comparison-pdf-btn"
          >
            {downloadingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            PDF
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Test Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Select Tests to Compare</CardTitle>
            <CardDescription>Choose 2-10 completed test sessions</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {tests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No completed tests found</p>
                ) : (
                  tests.map((test) => (
                    <div
                      key={test.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedTests.includes(test.id)
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-primary/50"
                      }`}
                      onClick={() => handleSelectTest(test.id)}
                      data-testid={`test-select-${test.id.slice(0,8)}`}
                    >
                      <Checkbox 
                        checked={selectedTests.includes(test.id)}
                        onCheckedChange={() => handleSelectTest(test.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{test.serial_number || "N/A"}</span>
                          {getStatusBadge(test.status)}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{test.product_name}</p>
                        <p className="text-xs text-muted-foreground">{formatDateIST(test.started_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Comparison Results
            </CardTitle>
            <CardDescription>
              {comparison ? `${comparison.test_count} tests compared` : "Select tests and click Compare"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!comparison ? (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <GitCompare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Select at least 2 tests to compare</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm font-medium">Passed</span>
                      </div>
                      <p className="text-2xl font-bold mt-1">{comparison.summary.total_passed}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <div className="flex items-center gap-2 text-red-600">
                        <TrendingDown className="w-4 h-4" />
                        <span className="text-sm font-medium">Failed</span>
                      </div>
                      <p className="text-2xl font-bold mt-1">{comparison.summary.total_failed}</p>
                    </div>
                  </div>

                  {/* Average Pass Rate */}
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <p className="text-sm font-medium text-muted-foreground">Average Pass Rate</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-3xl font-bold">{comparison.summary.average_pass_rate}%</p>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all"
                          style={{ width: `${comparison.summary.average_pass_rate}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Best/Worst Performing */}
                  {comparison.summary.best_performing_test && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg border">
                        <p className="text-xs text-muted-foreground">Best Performing</p>
                        <p className="font-medium">{comparison.summary.best_performing_test.serial}</p>
                        <p className="text-sm text-emerald-600">{comparison.summary.best_performing_test.pass_rate}%</p>
                      </div>
                      <div className="p-3 rounded-lg border">
                        <p className="text-xs text-muted-foreground">Worst Performing</p>
                        <p className="font-medium">{comparison.summary.worst_performing_test.serial}</p>
                        <p className="text-sm text-red-600">{comparison.summary.worst_performing_test.pass_rate}%</p>
                      </div>
                    </div>
                  )}

                  {/* Common Failures */}
                  {comparison.summary.common_failures.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Common Failure Points
                      </p>
                      <div className="space-y-2">
                        {comparison.summary.common_failures.map((failure, i) => (
                          <div 
                            key={`failure-${failure.step}-${i}`} 
                            className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{failure.step}</span>
                              <Badge variant="outline">{failure.failure_count} failures</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Affected: {failure.affected_tests.join(", ")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Test List */}
                  <div>
                    <p className="text-sm font-medium mb-2">Compared Tests</p>
                    <div className="space-y-2">
                      {comparison.tests.map((test, i) => (
                        <div key={test.serial_number || `test-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                          <div>
                            <span className="font-mono text-sm">{test.serial_number || <span className="text-muted-foreground font-sans">N/A</span>}</span>
                            <span className="text-xs text-muted-foreground ml-2">by {test.tester_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${
                              test.pass_rate >= 80 ? "text-emerald-600" : 
                              test.pass_rate >= 50 ? "text-amber-600" : "text-red-600"
                            }`}>
                              {test.pass_rate}%
                            </span>
                            {getStatusBadge(test.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
